<template>
  <component
    :is="element"
    class="product-prices"
    :class="[
      $blockClass(blockClass, 'prices'),
    ]"
  >
    <span
      class="product-prices__price"
      :class="[
        $blockClass(blockClass, 'price'),
        { 'product-prices__price--on-sale': formattedCompareAtPrice },
        priceClasses,
      ]"
      v-text="formattedPrice"
    />

    <span
      class="product-prices__compare-at-price"
      :class="[
        $blockClass(blockClass, 'compare-at-price'),
        compareAtPriceClasses,
      ]"
      v-text="formattedCompareAtPrice"
    />
  </component>
</template>

<script>

/**
 * Vue: Product prices
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {Boolean} available - If the product is available.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number|Boolean} compareAtPrice - Compare at price if it exists.
 * @param {String} [element] - Custom element override.
 * @param {Number} price - Product price, in cents format.
 * @param {String} [componentLocation] - Parent component using the product price snippet.
 */
export default {
  name: 'ProductPrices',

  props: {
    available: {
      type: Boolean,
      default: true,
    },
    blockClass: {
      type: String,
      default: '',
    },
    compareAtPrice: {
      type: [Number, Boolean],
      default: false,
      required: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    price: {
      type: [Number, Boolean],
      default: 0,
      required: true,
    },
    componentLocation: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      global: true,
    }
  },

  computed: {

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      return this.compareAtPrice && this.price < this.compareAtPrice
        ? this.$formatMoney(this.compareAtPrice)
        : ''
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.price === false) {
        return this.$string('accessibility.loading')
      }

      if (this.price <= 0) {
        return this.$string('product.prices.free')
      }

      return this.price ? this.$formatMoney(this.price) : ''
    },

    priceClasses() {
      switch (this.componentLocation) {
        case 'lineItem':
          return 'text-body-xsbold text-body-sbold-desktop'

        case 'productCard':
          return 'text-body-sbold text-heading-xs-desktop'

        case 'pdp':
          return 'text-heading-l text-heading-xl-desktop'

        case 'stickyATC':
          return 'text-heading-xl'

        case 'searchOverlayProductCard':
          return 'text-body-xsbold'
      }

      return ''
    },

    compareAtPriceClasses() {
      switch (this.componentLocation) {
        case 'lineItem':
          return 'text-body-xs'

        case 'productCard':
          return 'text-body-2xs'

        case 'pdp':
          return 'text-body-s text-body-m-desktop'

        case 'stickyATC':
          return 'text-body-m'

        case 'searchOverlayProductCard':
          return 'text-body-xs'
      }

      return ''
    },
  },
}
</script>
